import React, { useMemo } from "react"
import { useDevice } from "hooks/useDevice"
import PropTypes from "prop-types"
import moment from "moment"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import styles from "../../../Calendar.module.css"
import DayCell from "./DayCell"
import { useCalendarBlackouts } from "pagesV2/pro/Calendar/hooks/useCalendarBlackouts"
import { buildBookingSpans } from "pagesV2/pro/Calendar/utils"
import { buildBlackoutRuns, buildDayAlertSlots } from "pagesV2/pro/Calendar/blackoutUtils"

const DAY_KEY_FORMAT = "YYYY-MM-DD"
const dayKey = (m) => m.format(DAY_KEY_FORMAT)

/**
 * Day -> how many columns its blackout chip covers. A blackout running Oct 6-7 is one chip across
 * both columns, as the week views already draw it (`buildBlackoutRuns`): the run's first day gets
 * the run's length, the days it passes over get 0 and hold the row empty. Runs break at the week
 * row, the same as a multi-day booking bar.
 */
const toChipSpans = (weekDayKeys, getDay) =>
  buildBlackoutRuns(weekDayKeys, getDay).reduce((spans, run) => {
    const runStart = weekDayKeys.indexOf(run.dateKey)
    weekDayKeys.slice(runStart, runStart + run.span).forEach((key, offset) => {
      spans[key] = offset === 0 ? run.span : 0
    })
    return spans
  }, {})

const Month = ({ date, onChangeFilters, activeTab, crewIds, equipmentIds, categoryIds, roleIds, onOpenDayPanel }) => {
 const calendarDate = date
  const { calendarTimeOff, bookings } = useSelector((state) => state.calendar)
  const { t } = useTranslation()
  const { getDay: getDayBlackout } = useCalendarBlackouts()

  const monthStartDate = useMemo(() => moment(calendarDate).startOf("month"), [calendarDate])
  const monthEndDate = useMemo(() => moment(calendarDate).endOf("month"), [calendarDate])

  const gridStartDate = useMemo(() => moment(calendarDate).startOf("month").startOf("week"), [calendarDate])
  const gridEndDate = useMemo(() => moment(calendarDate).endOf("month").endOf("week"), [calendarDate])

  const calendarDaysCount = gridEndDate.diff(gridStartDate, "days") + 1

  const calendarDays = useMemo(
    () => new Array(calendarDaysCount).fill().map((_, index) => gridStartDate.clone().add(index, "days")),
    [gridStartDate, calendarDaysCount]
  )

  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet

  const weekDayNames = useMemo(
    () => new Array(7).fill().map((_, index) => moment().day(index).format("ddd")),
    []
  )

  const timeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = dayKey(day)
      const items = (calendarTimeOff || [])
        .filter((toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day"))
        .map((toff) => {
          const start = moment(toff.start_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])?.format("h:mm A")
          const end = moment(toff.end_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])?.format("h:mm A")
          return `${start} - ${end}`
        })
      acc[dayStr] = items
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  const allDayTimeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const isAllDay = calendarTimeOff?.some(
        (toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day") && Boolean(toff.all_day)
      )
      acc[dayKey(day)] = isAllDay || false
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  const bookingsByDay = useMemo(() => {
    return (bookings || []).reduce((acc, booking) => {
      if (!booking?.date) return acc
      acc[booking.date] = acc[booking.date] || []
      acc[booking.date].push(booking)
      return acc
    }, {})
  }, [bookings])

  const hasBookingsByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      acc[dayKey(day)] = Boolean(bookingsByDay[dayKey(day)]?.length)
      return acc
    }, {})
  }, [calendarDays, bookingsByDay])

  const needsCrewSelection = activeTab === "crew" && !crewIds?.length && !roleIds?.length
  const needsEquipmentSelection = activeTab === "equipment" && !equipmentIds?.length && !categoryIds?.length
  const showSelectionPrompt = needsCrewSelection || needsEquipmentSelection
  const selectionMessage = needsCrewSelection
    ? t("calendar.select_crew_member", "Select Crew Member")
    : t("calendar.select_equipment", "Select Equipment")

  return (
    <div className={styles.monthWrapper}>
      {showSelectionPrompt && (
        <div className={styles.monthSelectionOverlay}>
          <span className={styles.monthSelectionMessage}>{selectionMessage}</span>
        </div>
      )}
      <div className={styles.monthScrollWrapper}>
        <table className={styles.monthTable}>
          <thead>
            <tr className={styles.monthHeader}>
              {weekDayNames.map((dayName) => (
                <td key={dayName} className={styles.weekDayName}>
                  {isCompact ? dayName[0] : dayName}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, rowIdx) => {
              const weekDays = calendarDays.slice(rowIdx * 7, rowIdx * 7 + 7)
              const weekDayKeys = weekDays.map((day) => dayKey(day))
              const weekKey = weekDayKeys.join("-")
              // One bar per multi-day run, laid out across this row's columns.
              const { lanesByDay, laneCount } = buildBookingSpans(weekDayKeys, bookingsByDay)
              // The alert rows sit above those bars, so every column reserves the same ones.
              const weekBlackouts = weekDayKeys.map((key) => getDayBlackout(key))
              const alertSlots = buildDayAlertSlots(weekBlackouts)
              const chipSpans = toChipSpans(weekDayKeys, getDayBlackout)
              return (
                <tr key={weekKey}>
                  {weekDays?.map((day, colIdx) => {
                    const isPastDay = day.isBefore(moment().startOf("day"), "day")
                    const isCurrentMonth = day.month() === monthStartDate.month()
                    const isAllDayOff = allDayTimeOffByDay[dayKey(day)] && !hasBookingsByDay[dayKey(day)]
                    const hasAvailabilityIssue = Boolean(weekBlackouts[colIdx]?.blackouts?.length || weekBlackouts[colIdx]?.conflicts?.length)
                    return (
                      <td
                        key={dayKey(day)}
                        className={[
                          styles.monthTableCell,
                          isCurrentMonth ? "" : styles.notCurrentMonthDay,
                          isPastDay ? styles.pastDay : "",
                          // A day with a blackout chip already names the time off ("Crew OFF"), so it
                          // keeps the normal cell: the all-day style's line would sit on the chip, and
                          // its zero padding would stretch a spanning chip into the next day.
                          isAllDayOff && !hasAvailabilityIssue ? styles.monthTableCellAllDay : ""
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <DayCell
                          day={day}
                          thisMonth={isCurrentMonth}
                          lastCol={colIdx === 6}
                          lastRow={rowIdx === Math.ceil(calendarDays.length / 7) - 1}
                          monthStartDate={dayKey(monthStartDate)}
                          monthEndDate={dayKey(monthEndDate)}
                          timeOffRanges={timeOffByDay[dayKey(day)] || []}
                          allDayTimeOff={allDayTimeOffByDay[dayKey(day)]}
                          blackout={weekBlackouts[colIdx]}
                          spanLanes={lanesByDay[dayKey(day)]}
                          laneCount={laneCount}
                          columnIndex={colIdx}
                          alertSlots={alertSlots}
                          chipSpan={chipSpans[dayKey(day)]}
                          onOpenDayPanel={onOpenDayPanel}
                          onChangeFilters={onChangeFilters}
                          hideBookings={showSelectionPrompt}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

Month.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  activeTab: PropTypes.string,
  crewIds: PropTypes.array,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
  roleIds: PropTypes.array,
  onOpenDayPanel: PropTypes.func,
}

export default Month
